/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The rules of the kennisbank index — what the /kennisbank listing shows and
 * in which order — in ONE place, because the listing is built in more than one:
 * the rows of GET /api/articles and of the bake's seed (one builder,
 * src/lib/articlePayloads.js), the article section of llms.txt
 * (server/llmsTxt.ts), the feed and the CollectionPage. A rule that lives in
 * one of them and not the others is a list whose order disagrees with the page
 * it describes.
 *
 * Plain ESM JavaScript on purpose (the articleDates.js pattern): the prerender
 * script cannot import TypeScript. Keep it dependency-free but for plain-JS
 * siblings (the Amsterdam day key comes from amsterdamDay.js, the id tie-break
 * from nameOrder.js).
 *
 * Three rules live here:
 *   1. ORDER — newest first, by the date the card prints as "Gepubliceerd".
 *      Until 2026-09-23 the index followed the CMS drag order, which on
 *      production coincided with creation date ASCENDING: the newest article
 *      sat in the last slot. `order` stays in the data but nothing reads it.
 *   2. CATEGORY — a fixed set of ids; the labels are i18n keys
 *      (`kennisbank.category_<id>_v2`). An article carries one id or nothing.
 *   3. MEEST GELEZEN — the three most-read articles of the last 30 days,
 *      from the first-party pageview counter (site_events.json, per path per
 *      Amsterdam day). Ranked ids only qualify with at least one view, and
 *      the strip exists only when three qualify — a "most read" list padded
 *      with unread articles is a false claim.
 * And one number an article page prints, read the same way by the API and the
 * bake: its lifetime share count ({@link shareCountOf}).
 */

import { amsterdamDayKey } from './amsterdamDay.js';
import { compareCodePoints } from './nameOrder.js';

/** Category ids, in the order the chip row shows them. That order is Jasper's
 *  taxonomy of 2026-10-10 (ERE, Opbrengst en prijs, Inboekdienstverleners,
 *  Voorwaarden en toezicht, Laadpalen); the ids kept their names. */
export const ARTICLE_CATEGORIES = ['basics', 'price', 'providers', 'rules', 'charger'];

const CATEGORY_SET = new Set(ARTICLE_CATEGORIES);

/**
 * The article's category id, or null when it has none or carries an unknown
 * value (a retired id, a typo in a hand-edited file). Never throws.
 *
 * @param {{ category?: unknown } | null | undefined} article
 * @returns {string | null}
 */
export function articleCategoryOf(article) {
  const c = article && typeof article.category === 'string' ? article.category.trim() : '';
  return CATEGORY_SET.has(c) ? c : null;
}

/** i18n key of a category's label. Relabelled 2026-10-10 as `_v2` keys: the
 *  `category_<id>` keys are frozen in each environment's i18n_admin by past full
 *  CMS saves (docs/i18n-masking.md), so a new value on them never reaches prd.
 *  The ids themselves are unchanged — they are data on the articles, the
 *  `?categorie=` values and the `kennisbank_category_select` event value.
 *
 * @param {string} id
 * @returns {string}
 */
export function articleCategoryLabelKey(id) {
  return `kennisbank.category_${id}_v2`;
}

/** Query-string parameter names the listing reads. Both are on the SPA-shell
 *  list in server/spaRenderer.ts (URL_FILTER_PARAMS): a URL carrying one is
 *  never served the baked page, so the client can read them at render time. */
export const CATEGORY_PARAM = 'categorie';
export const PAGE_PARAM = 'pagina';

/** Cards per listing page. Three rows of the three-column grid. */
export const ARTICLES_PAGE_SIZE = 9;

/**
 * The view a listing URL asks for. An unknown category or a malformed page
 * falls back to the default silently: a stale or hand-typed link shows the
 * listing, never an error.
 *
 * @param {{ get(name: string): string | null } | null | undefined} params  URLSearchParams-like
 * @returns {{ category: string | null, page: number }}
 */
export function readListingView(params) {
  const category = articleCategoryOf({ category: params?.get(CATEGORY_PARAM) ?? '' });
  const raw = Number.parseInt(params?.get(PAGE_PARAM) ?? '', 10);
  return { category, page: Number.isFinite(raw) && raw >= 1 ? raw : 1 };
}

/**
 * The listing URL for a category and page. The default view (every category,
 * page 1) has exactly one spelling, the clean URL, because that is the baked
 * page and the canonical. Neither default is ever written into the query.
 *
 * @param {'nl'|'en'} lang
 * @param {{ category?: string | null, page?: number }} [view]
 * @returns {string}
 */
export function kennisbankListingHref(lang, view = {}) {
  // Both values are URL-safe by construction (a fixed set of lowercase ids, a
  // positive integer), so no encoding step — and no dependency on a
  // URLSearchParams global in the environments that import this module.
  const parts = [];
  const category = articleCategoryOf({ category: view.category });
  if (category) parts.push(`${CATEGORY_PARAM}=${category}`);
  const page = Number.isInteger(view.page) ? /** @type {number} */ (view.page) : 1;
  if (page > 1) parts.push(`${PAGE_PARAM}=${page}`);
  return `/${lang}/kennisbank${parts.length ? `?${parts.join('&')}` : ''}`;
}

/**
 * One page of a list. The requested page is clamped into range, so a link to
 * page 5 of a two-page list shows page 2 rather than an empty grid.
 *
 * @template T
 * @param {T[]} list
 * @param {number} page
 * @param {number} [size]
 * @returns {{ items: T[], page: number, pageCount: number }}
 */
export function paginate(list, page, size = ARTICLES_PAGE_SIZE) {
  const items = Array.isArray(list) ? list : [];
  const pageCount = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(Math.max(1, Math.trunc(page) || 1), pageCount);
  return { items: items.slice((current - 1) * size, current * size), page: current, pageCount };
}

/**
 * The categories at least one of these articles carries, in
 * ARTICLE_CATEGORIES order. The chip row offers only these, so a visitor can
 * never pick a filter that shows nothing.
 *
 * @param {Array<{ category?: unknown }>} articles
 * @returns {string[]}
 */
export function categoriesInUse(articles) {
  const used = new Set((Array.isArray(articles) ? articles : []).map(articleCategoryOf));
  return ARTICLE_CATEGORIES.filter((id) => used.has(id));
}

/** @param {unknown} value @returns {number} */
const parseTs = (value) => {
  const n = Date.parse(typeof value === 'string' ? value : '');
  return Number.isFinite(n) ? n : 0;
};

/**
 * Newest first by `createdAt`; a missing or unparsable date sorts last. Ties
 * (two articles created in the same millisecond — a bulk import) break on id
 * so every mirror produces the same sequence.
 *
 * @param {{ createdAt?: string, id?: string | number } | null | undefined} a
 * @param {{ createdAt?: string, id?: string | number } | null | undefined} b
 * @returns {number}
 */
export function compareNewestFirst(a, b) {
  const d = parseTs(b?.createdAt) - parseTs(a?.createdAt);
  if (d !== 0) return d;
  return compareCodePoints(a?.id, b?.id);
}

// --------------------------------------------------------------- meest gelezen

export const TOP_READ_WINDOW_DAYS = 30;
export const TOP_READ_COUNT = 3;
/** Prefix of every article pageview path in the counter (language-stripped). */
export const KENNISBANK_PATH_PREFIX = '/kennisbank/';

/**
 * Is this (language-stripped) path an article page?
 *
 * @param {unknown} path
 * @returns {boolean}
 */
export function isArticlePath(path) {
  return typeof path === 'string' && path.startsWith(KENNISBANK_PATH_PREFIX);
}

// Calendar maths on the label, not on milliseconds: subtracting 29 × 24 h
// across a DST change can land an hour into the wrong day. Same as
// shiftDayKey in server/data/dayKeys.ts.
/** @param {string} key @param {number} deltaDays @returns {string} */
const shiftDayKey = (key, deltaDays) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + deltaDays)).toISOString().slice(0, 10);
};

/**
 * Pageviews per article id over the window, summed over every slug the
 * article answers to: the Dutch slug, the English slug and the former slugs a
 * rename left behind (the counter keys on the path the visitor loaded, and a
 * renamed article keeps being read at its old address until the 301 catches
 * up in every index).
 *
 * @param {{ days?: Record<string, { pages?: Record<string, number> }> } | null | undefined} stats
 *   The site_events.json file shape.
 * @param {Array<{ id: string | number, slug?: string, slugEn?: string, formerSlugs?: string[] }>} articles
 * @param {number} nowTs
 * @param {number} [windowDays]
 * @returns {Map<string, number>} id → views (only ids with ≥ 1 view)
 */
export function sumArticleViews(stats, articles, nowTs, windowDays = TOP_READ_WINDOW_DAYS) {
  const idBySlug = new Map();
  for (const a of Array.isArray(articles) ? articles : []) {
    if (!a || a.id === undefined || a.id === null) continue;
    const id = String(a.id);
    const slugs = [a.slug, a.slugEn, ...(Array.isArray(a.formerSlugs) ? a.formerSlugs : [])];
    for (const s of slugs) {
      if (typeof s === 'string' && s.trim() && !idBySlug.has(s.trim())) idBySlug.set(s.trim(), id);
    }
  }
  const views = new Map();
  const days = stats && typeof stats === 'object' && stats.days ? stats.days : {};
  const toKey = amsterdamDayKey(nowTs);
  const fromKey = shiftDayKey(toKey, -(Math.max(1, windowDays) - 1));
  for (const [day, bucket] of Object.entries(days)) {
    if (day < fromKey || day > toKey) continue;
    const pages = bucket && typeof bucket === 'object' ? bucket.pages : null;
    if (!pages || typeof pages !== 'object') continue;
    for (const [path, count] of Object.entries(pages)) {
      if (!isArticlePath(path)) continue;
      const n = typeof count === 'number' && Number.isFinite(count) ? count : 0;
      if (n <= 0) continue;
      const slug = path.slice(KENNISBANK_PATH_PREFIX.length).replace(/\/+$/, '');
      const id = idBySlug.get(slug);
      if (!id) continue;
      views.set(id, (views.get(id) || 0) + n);
    }
  }
  return views;
}

/**
 * The "meest gelezen" ranking: the TOP_READ_COUNT most-viewed articles of the
 * window, most views first, a tie going to the newer article. Returns an
 * EMPTY list when fewer than TOP_READ_COUNT articles have a view at all —
 * the rule that keeps the strip off a fresh environment (acceptance has no
 * real traffic) instead of ranking three articles nobody read.
 *
 * @param {Parameters<typeof sumArticleViews>[0]} stats
 * @param {Array<{ id: string | number, slug?: string, slugEn?: string, formerSlugs?: string[], createdAt?: string }>} articles
 * @param {number} nowTs
 * @returns {Array<{ id: string, rank: number, views: number }>}
 */
export function rankTopRead(stats, articles, nowTs) {
  const list = Array.isArray(articles) ? articles : [];
  const views = sumArticleViews(stats, list, nowTs);
  const byId = new Map(list.map((a) => [String(a?.id), a]));
  const ranked = [...views.entries()]
    .map(([id, n]) => ({ id, views: n, article: byId.get(id) }))
    .sort((a, b) => b.views - a.views || compareNewestFirst(a.article, b.article))
    .slice(0, TOP_READ_COUNT);
  if (ranked.length < TOP_READ_COUNT) return [];
  return ranked.map((r, i) => ({ id: r.id, rank: i + 1, views: r.views }));
}

/**
 * The `topRead` field of one index row: `{ rank, views }` for a ranked
 * article, null for every other — what the listing rows of the API and the
 * bake carry (articleIndexRows in src/lib/articlePayloads.js).
 *
 * @param {ReturnType<typeof rankTopRead>} ranking
 * @param {string | number} id
 * @returns {{ rank: number, views: number } | null}
 */
export function topReadOf(ranking, id) {
  const hit = ranking.find((r) => r.id === String(id));
  return hit ? { rank: hit.rank, views: hit.views } : null;
}

/**
 * The lifetime share count of one article — the "N× gedeeld" on its page —
 * from article_shares.json (server/data/articleShares.ts folds the pings into
 * it). GET /api/articles/:slug and the bake read it through this one rule; an
 * unusable file or bucket counts 0.
 *
 * @param {unknown} stats  the parsed article_shares.json
 * @param {unknown} articleId
 * @returns {number}
 */
export function shareCountOf(stats, articleId) {
  /** @type {any} */
  const file = stats;
  const byArticle =
    file && typeof file === 'object' && file.byArticle && typeof file.byArticle === 'object'
      ? file.byArticle
      : {};
  const bucket = byArticle[String(articleId)];
  return bucket && Number.isFinite(bucket.total) ? bucket.total : 0;
}
